import { useEffect, useRef } from "react";
import styles from "./overlay-toolbox.module.css";
import { createPortal } from "react-dom";
import { Rect, Point } from "PosterEditor/model/poster-types";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { UIIconName } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-type";

export type ContextType = "point" | "staticLabel" | "backdrop" | "photo";

const titleForContextType = (contextType: ContextType): string => {
  switch (contextType) {
    case "point":
      return "Add map label";
    case "staticLabel":
      return "Add floating label";
    case "backdrop":
      return "Add backdrop";
    case "photo":
      return "Add photo";
  }
};

const iconNameForContextType = (contextType: ContextType): UIIconName => {
  switch (contextType) {
    case "point":
      return "MAP-LABEL";
    case "staticLabel":
      return "STATIC-LABEL";
    case "backdrop":
      return "BACKDROP";
    case "photo":
      return "PHOTO";
  }
};

interface OverlayToolboxProps {
  position: Point;
  mapRect: Rect;
  onClose: () => void;
  onSelectContextType: (contextType: ContextType) => void;
}

export const OverlayToolbox = (props: OverlayToolboxProps) => {
  const { isTemplateEditing } = usePosterProject();
  const toolboxRef = useRef<any>(null);

  let contextTypes: ContextType[] = ["point"];

  if (isTemplateEditing) {
    contextTypes = ["point", "staticLabel", "backdrop", "photo"];
  }

  const getPosition = (): { x: number; y: number } => {
    return {
      x: (props.position.x ?? 0) + props.mapRect.x - 10,
      y: (props.position.y ?? 0) + props.mapRect.y - 25,
    };
  };

  const position: { x: number; y: number } = getPosition();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const fontPickerRef = document.getElementById("dropdown-portal");
      if (
        toolboxRef.current &&
        !toolboxRef.current.contains(event.target as Node) &&
        !(fontPickerRef && fontPickerRef.contains(event.target as Node)) // Ensure clicks inside font picker do not close toolbox
      ) {
        props.onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props]);

  return createPortal(
    <div
      className={styles.paddedContainer}
      ref={toolboxRef}
      style={{
        position: "absolute",
        top: `${position.y}px`,
        left: `${position.x}px`,
        transform: "translate(-0%, -0%)", // Optional: to center the menu at the pointer location
      }}
    >
      <div className={styles.container}>
        {contextTypes.map((contextType) => (
          <button
            key={contextType}
            className={styles.contextButton}
            onClick={() => {
              props.onSelectContextType(contextType as ContextType);
              props.onClose();
            }}
          >
            <UIIcon
              iconName={iconNameForContextType(contextType)}
              variant="REGULAR"
              size={22}
            />
            {titleForContextType(contextType)}
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
};
